/** MBCB staff or assigned hub leader for this planter may edit regardless of workflow status. */
export const canManageMbcbReportForPlanter = (
  userId: number | undefined,
  planterAssignedHubLeader: number | null | undefined,
  roleNames: string[],
): boolean => {
  const isStaffish = roleNames.some((r) => ['admin', 'staff', 'mbcb'].includes(r));
  if (isStaffish) return true;
  return (
    roleNames.includes('hub_leader') &&
    planterAssignedHubLeader != null &&
    userId != null &&
    planterAssignedHubLeader === userId
  );
};

export const canShowMbcbReviewActions = (status?: string | null): boolean =>
  status === 'submitted' || status === 'under_review';

export const canEditMbcbReport = (
  status?: string | null,
  hasManagerEditAccess: boolean = false,
): boolean => {
  if (hasManagerEditAccess) return true;
  return status === 'draft' || status === 'needs_revision';
};

const monthFormatter = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  year: 'numeric',
});

export const formatMbcbReportPeriod = (month?: number | null, year?: number | null): string => {
  if (!month || !year) return 'Unknown month';
  return monthFormatter.format(new Date(year, month - 1, 1));
};

/**
 * Ministry month stored on MBCB reports (`reportMonth` / `reportYear`): the calendar month
 * being reported on — typically one month before the filing (named) month.
 * Matches backend `getPreviousMonthPeriod` in mbcbReportGenerationService.js.
 */
export const getPreviousMonthMinistryPeriod = (fromDate: Date = new Date()) => {
  const year = fromDate.getFullYear();
  const month = fromDate.getMonth() + 1;
  if (month === 1) {
    return { year: year - 1, month: 12 };
  }
  return { year, month: month - 1 };
};

/** Filing calendar month/year when submitting today (the month the report is named for). */
export const getCurrentFilingMonthYear = (fromDate: Date = new Date()) => ({
  month: fromDate.getMonth() + 1,
  year: fromDate.getFullYear(),
});

const getFilingMonthYearAfterMinistry = (ministryMonth: number, ministryYear: number) => {
  if (ministryMonth === 12) return { month: 1, year: ministryYear + 1 };
  return { month: ministryMonth + 1, year: ministryYear };
};

/** Human-readable filing month label (e.g. "April 2026") for a stored ministry period. */
export const formatMbcbFilingMonthLabel = (ministryMonth: number, ministryYear: number): string => {
  const { month, year } = getFilingMonthYearAfterMinistry(ministryMonth, ministryYear);
  return formatMbcbReportPeriod(month, year);
};

/** Same title planters see in the app, e.g. "April 2026 report". */
export const mbcbReportNamedTitleFromMinistry = (
  ministryMonth: number,
  ministryYear: number,
): string => `${formatMbcbFilingMonthLabel(ministryMonth, ministryYear)} report`;

export const mbcbReportActionLabel = (status?: string | null): string => {
  if (status === 'draft') return 'Open report';
  if (status === 'needs_revision') return 'Continue report';
  return 'View report';
};

export const mbcbReportStatusLabel = (status?: string | null): string => {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ');
};

const WORSHIP_SUNDAY_KEYS = ['s1', 's2', 's3', 's4', 's5'] as const;

/** Count Sundays in a calendar month (local date semantics). Matches backend `sundaysInMonth`. */
const sundaysInMonth = (month: number, year: number): number => {
  const days = new Date(year, month, 0).getDate();
  let n = 0;
  for (let d = 1; d <= days; d += 1) {
    if (new Date(year, month - 1, d).getDay() === 0) n += 1;
  }
  return n;
};

/**
 * Average Sunday worship attendance for one MBCB report: average of the weeks actually
 * reported (zero/blank weeks excluded), falling back to the legacy `sundayAttendance` total
 * divided by Sundays-in-month when no weekly breakdown is present. Matches backend
 * `mbcbReportAvgSundayAttendance` in planterPipelineService.js and iOS
 * `PlanterDetailView.attendanceAverage`, so this page shows the same "average" the network-wide
 * rollups use instead of a raw month total.
 */
export const mbcbReportAvgSundayAttendance = (report: {
  weeklyMetrics?: unknown;
  sundayAttendance?: number | null;
  reportMonth?: number | null;
  reportYear?: number | null;
}): number => {
  const wm = (report.weeklyMetrics ?? {}) as Record<string, unknown>;
  const worship = wm.worshipAttendance;
  if (worship && typeof worship === 'object') {
    const row = worship as Record<string, unknown>;
    const positives = WORSHIP_SUNDAY_KEYS.map((k) => Number(row[k])).filter(
      (n) => Number.isFinite(n) && n > 0,
    );
    if (positives.length > 0) {
      return positives.reduce((a, b) => a + b, 0) / positives.length;
    }
  }
  const total = Number(report.sundayAttendance ?? 0);
  const month = report.reportMonth;
  const year = report.reportYear;
  if (total > 0 && month != null && month >= 1 && month <= 12 && year != null && year >= 2000) {
    return total / Math.max(sundaysInMonth(month, year), 1);
  }
  return 0;
};
